import React, { Suspense, useEffect, useState, useRef } from "react";

import homePagePayload from "../../config/homePayload";
import homePageAPI from "../../api/homePageApi";

import CouponFlash from "./CouponFlash";
import { Link, useHref } from "react-router-dom";
import { Row, Col, Container } from "react-bootstrap";

import {
  Swiper,
  SwiperSlide,
  Autoplay,
  Navigation,
  Pagination,
} from "./Shared/CustomSwiper";
import { FaBox, FaCheckCircle, FaHeadset } from "react-icons/fa";
import { ArrowRight, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

const Product = React.lazy(() => import("../products/Card"));
const Testimonials = React.lazy(() => import("../testimonials/Card"));
const CategoryCard = React.lazy(() => import("../categories/Card"));
const Blog = React.lazy(() => import("../blogs/Card"));
const Banner = React.lazy(() => import("./Banner"));

function Home() {
  const [coupons, setCoupons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [products, setProducts] = useState([]);
  const [topSellerProducts, setTopSellerProducts] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [topBanners, setTopBanners] = useState([]);
  const [middleBanners, setMiddleBanners] = useState([]);
  const firstMiddleBanner = middleBanners[0];
  const secondMiddleBanner = middleBanners[1];
  const thirdMiddleBanner = middleBanners[2];
  const bannerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);


  const parallaxRef = useRef(null);
  const [parallaxOffset, setParallaxOffset] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!parallaxRef.current) return;
      const rect = parallaxRef.current.getBoundingClientRect();
      const viewportCenter = window.innerHeight / 2;
      const elementCenter = rect.top + rect.height / 2;
      const distance = viewportCenter - elementCenter;
      setParallaxOffset(distance * 0.15); // 0.15 = parallax speed
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    fetchHomePageDetails();
  }, []);

  const CACHE_TIME = 3 * 60 * 1000;

  const applyData = (data) => {
    setTopBanners(data?.banners?.filter((b) => b.position === "top"));
    setMiddleBanners(data?.banners?.filter((b) => b.position === "middle"));
    setCoupons(data?.coupons);
    setCategories(data?.categories);
    setTestimonials(data?.testimonials);
    setBlogs(data?.blogs);
    setProducts(data?.products);
    setTopSellerProducts(data?.topSellerProducts);
  };

  const fetchHomePageDetails = async () => {
    try {
      const cached = sessionStorage.getItem("homeData");
      const parsed = cached ? JSON.parse(cached) : null;

      if (parsed) applyData(parsed.data);

      if (!parsed || Date.now() - parsed.timestamp > CACHE_TIME) {
        const res = await homePageAPI.homePage(homePagePayload);
        if (res.code === 200) {
          sessionStorage.setItem(
            "homeData",
            JSON.stringify({ data: res.data, timestamp: Date.now() }),
          );
          if (!parsed) applyData(res.data); // only update state if no cache was shown
        }
      }
    } catch (error) {
      console.error("Home API Error:", error);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // This allows the animation to reset and run again every time it enters view
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.2 } // Runs when 20% of the banner is visible
    );

    if (bannerRef.current) {
      observer.observe(bannerRef.current);
    }

    return () => {
      if (bannerRef.current) observer.unobserve(bannerRef.current);
    };
  }, []);

  const prevRef = useRef(null);
  const nextRef = useRef(null);

  // home videos
  const videos = [
    `${process.env.PUBLIC_URL}/img/video1.mp4`,
    `${process.env.PUBLIC_URL}/img/video2.mp4`,
    `${process.env.PUBLIC_URL}/img/video3.mp4`,
    `${process.env.PUBLIC_URL}/img/video4.mp4`,
  ];

  return (
    <>
      {topBanners && topBanners.length > 0 && (
        <div>
          <Banner item={topBanners} />
        </div>
      )}



      {coupons && coupons.length > 0 && (
        <section className="custom-coupon-section">
          <div className="custom-coupon-marquee-wrap">
            <div className="custom-coupon-marquee">
              {coupons.map((coupon, index) => (
                <CouponFlash key={coupon._id || index} item={coupon} />
              ))}


              {coupons.map((coupon, index) => (
                <CouponFlash
                  key={`duplicate-${coupon._id || index}`}
                  item={coupon}
                />
              ))}
            </div>
          </div>
        </section>
      )}


      {categories && categories.length > 0 && (
        <section className="custom-category-section py-5">
          <div className="container">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h2 className="section-title">Explore Categories</h2>
              <Link to="/categories" className="view-all-btn">
                View More
              </Link>
            </div>

            <Suspense fallback={<div></div>}>
              <Swiper
                modules={[Autoplay]}
                autoplay={{
                  delay: 2500,
                  disableOnInteraction: false,
                }}
                loop={true}
                centeredSlides={false}
                spaceBetween={25}
                breakpoints={{
                  0: {
                    slidesPerView: 1.2,
                  },
                  480: {
                    slidesPerView: 2,
                  },
                  768: {
                    slidesPerView: 3,
                  },
                  1200: {
                    slidesPerView: 4,
                  },
                }}
              >
                {categories.map((category, index) => (
                  <SwiperSlide key={index}>
                    <CategoryCard item={category} />
                  </SwiperSlide>
                ))}
              </Swiper>
            </Suspense>
          </div>
        </section>
      )}


      {/* <section className="custom-ax-pakh-sec-parlax-container">
  <div className="custom-ax-pakh-sec-parlax-bg">
    <video autoPlay loop muted playsInline poster="your-fallback-image.jpg">
      <source src="/images/video2.mp4" type="video/mp4" />
      Your browser does not support the video tag.
    </video>
  </div>
  <div className="content">
    <h1>Saree Collection</h1>
  </div>
</section> */}



      {products && products.length > 0 && (
        <section className="bestsellers-section main-section py-5">
          <div className="container ">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h2 className="section-title">Bestselling Products</h2>
              <Link to="/shop" className="view-all-btn">
                View All
              </Link>
            </div>
            <Row className="g-3">
              <Suspense fallback={<div></div>}>
                {products.map((product, index) => (
                  <Col
                    lg={3}
                    md={4}
                    sm={6}
                    xs={6}
                    key={[product._id || index]}
                    className="mb-4"
                  >
                    <Product item={product} />
                  </Col>
                ))}
              </Suspense>
            </Row>
          </div>
        </section>
      )}




      {/* {firstMiddleBanner && (
  <div className="premium-banner-wrapper" ref={bannerRef}>
    <div className="container">
      <div className="row align-items-center">

     
        <div className="col-lg-6 col-md-6 col-12">
          <div className="image-section text-center">
            <div className="silver-frame"></div>
            <img
              src={firstMiddleBanner.image}
              alt="Ethnic Model"
              className="model-img img-fluid"
            />
          </div>
        </div>

   
        <div className="col-lg-6 col-md-6 col-12">
          <div className="content-section">
            <h2 className="stylish-text">
              {firstMiddleBanner.title}
            </h2>

            <p className="subtitle">
              {firstMiddleBanner.subTitle}
            </p>
          </div>
        </div>

      </div>
    </div>
  </div>
)} */}



      {firstMiddleBanner && (
        <section className="premium-banner-wrapper" ref={bannerRef}>
          <div className="container">
            <div className="row align-items-center gy-5">

              {/* Left Image */}
              <div className="col-lg-6   col-12">
                <div className="premium-image-wrapper">
                  <div className="image-bg-shape"></div>

                  <img
                    src={firstMiddleBanner.image}
                    alt={firstMiddleBanner.title}
                    className="premium-banner-image "
                  />

                  <div className="experience-badge">
                    <span>Premium</span>
                    <h5>Handcrafted</h5>
                  </div>
                </div>
              </div>

              {/* Right Content */}
              <div className="col-lg-6   col-12">
                <div className="premium-content">

                  <span className="section-tag">
                    ✦ Timeless Elegance
                  </span>

                  <h2 className="premium-title">
                    {firstMiddleBanner.title}
                  </h2>

                  <p className="premium-subtitle">
                    {firstMiddleBanner.subTitle}
                  </p>

                  <div className="premium-divider"></div>

                  {/* <p className="premium-subtitle">
                    {firstMiddleBanner.subTitle}
                  </p> */}

                  <Link to="/shop"><button className="premium-btn">
                    Explore Collection
                  </button>
                  </Link>
                </div>
              </div>

            </div>
          </div>
        </section>
      )}







      {topSellerProducts && topSellerProducts.length > 0 && (
        <section className="bestsellers-section main-section py-5">
          <div className="container ">
            <div className="d-flex align-items-center justify-content-between mb-4">
              <h2 className="section-title">Trending Products</h2>
              <Link to="/shop" className="view-all-btn">
                View All
              </Link>
            </div>
            <Row className="g-3">
              <Suspense fallback={<div></div>}>
                {topSellerProducts.map((product, index) => (
                  <Col
                    lg={3}
                    md={6}
                    sm={6}
                    xs={6}
                    key={[product._id || index]}
                    className="mb-4"
                  >
                    <Product item={product} />
                  </Col>
                ))}
              </Suspense>
            </Row>
          </div>
        </section>
      )}

      {/* second middle section */}
      {/* {thirdMiddleBanner && (
        <div className="middle-banner">
       
          <div
            className="middle-banner-bg"
            style={{
              backgroundImage: `url(${thirdMiddleBanner.image})`,
            }}
          />

     
          <div className="middle-banner-overlay" />

  
          <div className="middle-banner-content-one">
            <Container>
              <span className="spotlight-tagline">Traditional Attire</span>
              <h2 className="spotlight-heading">{thirdMiddleBanner.title}</h2>
              <p className="spotlight-description">
                {thirdMiddleBanner.subTitle}
              </p>
              <Link to="/shop" className="spotlight-btn">
                Discover More
              </Link>
            </Container>
          </div>
        </div>
      )} */}

      {testimonials && testimonials.length > 0 && (
        <section className="testimonials-section py-5   ">
          <Container>
            <div className="text-left mb-4">
              <h2 className="section-title">What Our Customer Says</h2>
            </div>

            <Swiper
              modules={[Autoplay]}
              autoplay={{
                delay: 3500,
                disableOnInteraction: false,
              }}
              loop={true}
              spaceBetween={25}
              breakpoints={{
                0: {
                  slidesPerView: 1.1,
                },
                576: {
                  slidesPerView: 1.5,
                },
                768: {
                  slidesPerView: 2,
                },
                1200: {
                  slidesPerView: 3,
                },
              }}
            >
              <Suspense fallback={<div></div>}>
                {testimonials.map((testimonial, index) => (
                  <SwiperSlide key={index}>
                    <Testimonials item={testimonial} />
                  </SwiperSlide>
                ))}
              </Suspense>
            </Swiper>
          </Container>
        </section>
      )}

      {/* middle banners */}
      {/* {secondMiddleBanner && (
        <div className="editorial-banner" ref={parallaxRef}>
          <div className="container-fluid">
            <div className="banner-grid">
              <div className="editorial-content">
                <div>
                  <span className="heritage-tag">Ethnic Wear</span>
                  <h1 className="editorial-h1">
                    <span>{secondMiddleBanner.title}</span>
                  </h1>
                  <div className="desc-box">{secondMiddleBanner.subTitle}</div>

                  <Link
                    to="/shop"
                    className="cta-editorial shadow-sm text-decoration-none"
                  >
                    VIEW MORE <ArrowRight size={18} />
                  </Link>
                </div>
              </div>

         
              <div className="image-architecture">
                <div className="floating-frame shadow-lg">
                  <img
                    src={secondMiddleBanner.image}
                    alt="Luxury Heritage Wear"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )} */}

      {blogs && blogs.length > 0 && (
        <section className="blog-section py-5  ">
          <Container>
            <div className="d-flex justify-content-between align-items-end mb-4">
              <div>
                <h2 className="mb-0 section-title">Latest Stories</h2>
              </div>
              <Link to="/blog" className="view-all-btn">
                View All
              </Link>
            </div>

            <Swiper
              modules={[Pagination, Autoplay]}
              autoplay={{
                delay: 3500,
                disableOnInteraction: false,
              }}
              loop={true}
              spaceBetween={25}
              pagination={{ clickable: true }}
              breakpoints={{
                0: {
                  slidesPerView: 1.1,
                },
                576: {
                  slidesPerView: 1.5,
                },
                768: {
                  slidesPerView: 2,
                },
                1200: {
                  slidesPerView: 3,
                },
              }}
              className="blog-swiper pt-3"
            >
              <Suspense fallback={<div></div>}>
                {blogs.map((blog, index) => (
                  <SwiperSlide key={blog.id}>
                    <Blog item={blog} />
                  </SwiperSlide>
                ))}
              </Suspense>
            </Swiper>
          </Container>
        </section>
      )}
    </>
  );
}

export default Home;
